import type { GenerationSettings } from '../stores/projects-store';
import type { SectionType, MobileSectionType, DensityType, AnySection } from '../stores/sections-builder-store';
import type { AppLanguage } from '../stores/language-store';
import { t } from '../i18n/translations';

const sectionPromptKey: Record<SectionType, 'promptSectionHeader' | 'promptSectionHero' | 'promptSectionFeatures' | 'promptSectionCards' | 'promptSectionTestimonials' | 'promptSectionPricing' | 'promptSectionFaq' | 'promptSectionBottomNav' | 'promptSectionFooter'> = {
  header: 'promptSectionHeader',
  hero: 'promptSectionHero',
  features: 'promptSectionFeatures',
  cards: 'promptSectionCards',
  testimonials: 'promptSectionTestimonials',
  pricing: 'promptSectionPricing',
  faq: 'promptSectionFaq',
  bottomNav: 'promptSectionBottomNav',
  footer: 'promptSectionFooter',
};

const mobileSectionPromptKey: Record<MobileSectionType, 'promptMobileSectionTabBar' | 'promptMobileSectionDrawer' | 'promptMobileSectionSplash' | 'promptMobileSectionListFeed' | 'promptMobileSectionDetail' | 'promptMobileSectionForm' | 'promptMobileSectionModal' | 'promptMobileSectionLoading' | 'promptMobileSectionEmpty' | 'promptMobileSectionError'> = {
  tabBar: 'promptMobileSectionTabBar',
  drawer: 'promptMobileSectionDrawer',
  splashScreen: 'promptMobileSectionSplash',
  listFeed: 'promptMobileSectionListFeed',
  detailScreen: 'promptMobileSectionDetail',
  form: 'promptMobileSectionForm',
  modal: 'promptMobileSectionModal',
  loadingState: 'promptMobileSectionLoading',
  emptyState: 'promptMobileSectionEmpty',
  errorScreen: 'promptMobileSectionError',
};

const densityPromptKey: Record<DensityType, 'promptDensityMinimal' | 'promptDensityStandard' | 'promptDensityRich'> = {
  minimal: 'promptDensityMinimal',
  standard: 'promptDensityStandard',
  rich: 'promptDensityRich',
};

function isWebSection(section: AnySection): section is SectionType {
  return ['header', 'hero', 'features', 'cards', 'testimonials', 'pricing', 'faq', 'bottomNav', 'footer'].includes(section);
}

function isMobileSection(section: AnySection): section is MobileSectionType {
  return ['tabBar', 'drawer', 'splashScreen', 'listFeed', 'detailScreen', 'form', 'modal', 'loadingState', 'emptyState', 'errorScreen'].includes(section);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.trim();
  const m = /^#([0-9a-f]{6})$/i.exec(h);
  if (!m) return null;
  const v = m[1];
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function relativeLuminance(rgb: { r: number; g: number; b: number }) {
  // WCAG relative luminance
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const R = toLinear(rgb.r);
  const G = toLinear(rgb.g);
  const B = toLinear(rgb.b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function isLightHex(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  // ~0.5 is a reasonable cutoff for "light" backgrounds.
  return relativeLuminance(rgb) >= 0.5;
}

export function buildSectionsPrompt(
  sections: AnySection[],
  density: DensityType,
  language: AppLanguage
): string {
  if (sections.length === 0) return '';

  const sectionsList = sections
    .map((s) => {
      if (isWebSection(s)) {
        return t(language, sectionPromptKey[s]);
      } else if (isMobileSection(s)) {
        return t(language, mobileSectionPromptKey[s]);
      }
      return '';
    })
    .filter(Boolean)
    .join(', ');

  const densityText = t(language, densityPromptKey[density]);
  const isApp = sections.some(isMobileSection);

  let prompt = '';

  if (language === 'ru') {
    const title = isApp ? '[СТРУКТУРА ПРИЛОЖЕНИЯ]' : '[СТРУКТУРА СТРАНИЦЫ]';
    const instruction = isApp
      ? `Спроектируй мобильное приложение со следующими экранами/компонентами: ${sectionsList}.`
      : `Создай страницу со следующими секциями: ${sectionsList}.`;

    prompt = `\n\n${title}\n${instruction}\nСтиль наполнения: ${densityText}.`;

    if (isApp) {
      prompt += `\n\n[ТРЕБОВАНИЯ К МОБИЛЬНОМУ ПРИЛОЖЕНИЮ]`;
      prompt += `\n- Root контейнер ДОЛЖЕН быть w-full без padding и margin`;
      prompt += `\n- Tab Bar/Bottom Nav: ВСЕГДА fixed bottom-0, w-full, равномерное распределение иконок без отступов`;
      prompt += `\n- Все компоненты по краям должны быть edge-to-edge, без гаечных полей`;
      prompt += `\n- Header/Search Bar: fixed top-0 если нужен, w-full, no margins`;
      prompt += `\n- Основной контент: начинается ниже fixed элементов, не перекрывается`;
      prompt += `\n- Иконки в навигации: простые, четкие, узнаваемые (Home, Shop, Search, Profile и т.д.)`;
      prompt += `\n- Tab Bar items: 4-5 пунктов, с иконками и labels, центрированные по вертикали`;
      prompt += `\n- НИКАКОГО JavaScript: не используй <script> и inline onClick/onLoad`;
      prompt += `\n- По умолчанию ДОЛЖЕН быть виден основной экран (feed/list). Splash/detail/modal/drawer должны быть hidden по умолчанию и не перекрывать основной экран.`;
      prompt += `\n- Любая иконка-кнопка без текста ДОЛЖНА иметь aria-label`;
      prompt += `\n- По умолчанию НЕ используй glassmorphism: запрети backdrop-filter/backdrop-blur и прозрачные фоны типа bg-white/50; используй плотные solid фоны (bg-white, bg-slate-50 и т.д.).`;
    }
  } else {
    const title = isApp ? '[APP STRUCTURE]' : '[PAGE STRUCTURE]';
    const instruction = isApp
      ? `Design a mobile app with the following screens/components: ${sectionsList}.`
      : `Create a page with the following sections: ${sectionsList}.`;

    prompt = `\n\n${title}\n${instruction}\nContent density style: ${densityText}.`;

    if (isApp) {
      prompt += `\n\n[MOBILE APP REQUIREMENTS]`;
      prompt += `\n- Root container MUST be w-full with NO padding or margin`;
      prompt += `\n- Tab Bar/Bottom Nav: ALWAYS fixed bottom-0, w-full, evenly distributed icons with no gaps`;
      prompt += `\n- All edge components must be edge-to-edge, no side margins`;
      prompt += `\n- Header/Search Bar: fixed top-0 if needed, w-full, no margins`;
      prompt += `\n- Main content: starts below fixed elements, no overlap`;
      prompt += `\n- Navigation icons: simple, clear, recognizable (Home, Shop, Search, Profile, etc.)`;
      prompt += `\n- Tab Bar items: 4-5 items with icons and labels, vertically centered`;
      prompt += `\n- NO JavaScript: do not include <script> tags or inline onClick/onLoad attributes`;
      prompt += `\n- Default visible screen MUST be the main feed/list. Splash/detail/modal/drawer MUST be hidden by default and must not block the main screen.`;
      prompt += `\n- Any icon-only button MUST have aria-label`;
      prompt += `\n- Avoid glassmorphism by default: do NOT use backdrop-filter/backdrop-blur or translucent backgrounds like bg-white/50; prefer solid backgrounds (bg-white, bg-slate-50, etc).`;
    }

    // Special handling for Tab Bar
    if (sections.includes('tabBar')) {
      prompt += `\n\n[TAB BAR SPECIFICS]`;
      prompt += `\n- Fixed position at bottom-0, w-full, h-20`;
      prompt += `\n- 4-5 navigation items with equal width (flex grow)`;
      prompt += `\n- Each item: icon (20-24px) + small label below`;
      prompt += `\n- Active item: highlight with accent color or filled icon`;
      prompt += `\n- Inactive items: subtle gray or muted color`;
      prompt += `\n- No gaps or padding at screen edges - full bleed`;
    }
  }

  if (language === 'ru' && sections.includes('tabBar')) {
    prompt += `\n\n[СПЕЦИФИКА TAB BAR]`;
    prompt += `\n- Fixed позиция в bottom-0, w-full, h-20`;
    prompt += `\n- 4-5 пунктов навигации с равной шириной (flex grow)`;
    prompt += `\n- Каждый пункт: иконка (20-24px) + маленький label снизу`;
    prompt += `\n- Активный пункт: подсветка accent цветом или заполненная иконка`;
    prompt += `\n- Неактивные: серый или muted цвет`;
    prompt += `\n- Без отступов на краях - полная ширина экрана`;
  }

  return prompt;
}

export function constructPromptWithSettings(userPrompt: string, settings?: GenerationSettings): string {
    if (!settings) return userPrompt;
    // If caller already included the block (e.g. user pasted it), avoid duplicating.
    if (userPrompt.includes('[DESIGN GENERATION SETTINGS]')) return userPrompt;

    let instructions = '\n\n[DESIGN GENERATION SETTINGS]';

    // Devices
    if (settings.devices && settings.devices.length > 0) {
        instructions += `\n- Target Devices: ${settings.devices.join(', ')}.`;

        instructions += ' Use Tailwind CSS responsive prefixes effectively.';
        if (settings.devices.includes('mobile')) {
            instructions += '\n- Mobile-First Architecture: Elements MUST be edge-to-edge (0 margin/padding on the root container) to feel like a native mobile app. DO NOT wrap the entire content in a "container" or "p-4" div. Headers, Hero sections, and Bottom Navbars MUST be `w-full` and touch the screen edges.';
            instructions += '\n- Header & Nav: Top headers should be `sticky` or `fixed` top-0. Bottom navbars should be `fixed` bottom-0. Ensure they span 100% width. NO empty gaps or margins at the very top/bottom of the screen.';
            instructions += ' Base styles (no prefix) MUST be optimized for mobile screens.';
        }
        if (settings.devices.includes('tablet')) {
            instructions += ' Use `md:` prefix for tablet-specific adjustments.';
        }
        if (settings.devices.includes('desktop')) {
            instructions += ' Use `lg:` or `xl:` prefixes for desktop layouts.';
        }
    }

    // Detail Level
    if (settings.detailLevel) {
        const level = settings.detailLevel;
        instructions += `\n- Detail & Fidelity Level: ${level}/5.`;

        if (level === 1) {
            instructions += ' create a very low-fidelity, skeletal wireframe. Use outlines, greyscale or minimal colors, and placeholder text/boxes. Focus on layout structure only.';
        } else if (level === 2) {
            instructions += ' create a low-fidelity mockups. Simple styling, flat colors, standard browser fonts or simple sans-serif.';
        } else if (level === 3) {
            instructions += ' create a standard UI design. balanced usage of shadows, card layouts, and standard modern aesthetics. Clean and functional.';
        } else if (level === 4) {
            instructions += ' create a polished, high-quality UI. Use subtle gradients, refined shadows, rounded corners, and good whitespace.';
        } else if (level === 5) {
            instructions += ' create an Ultra-Premium, High-Fidelity design. Use advanced CSS effects (glassmorphism/backdrop-filter), complex gradients, vibrant and sophisticated color usage, smooth micro-interactions (hover states), and modern typography choices.';
        }
    }

    // Colors
    if (settings.colors && settings.colors.length > 0) {
        instructions += `\n- Color Palette: The primary/base brand colors are: [${settings.colors.join(', ')}].`;
        instructions += ' Use these as your anchor colors. You are encouraged to expand this into a full, harmonious color system. Generate appropriate shades, tints, and complementary colors that work well with the base palette to create a professional and balanced UI.';
    }

    // App background
    if (settings.backgroundColor && /^#[0-9a-f]{6}$/i.test(settings.backgroundColor.trim())) {
        const bg = settings.backgroundColor.trim();
        const light = isLightHex(bg);
        instructions += `\n- App Background Color: ${bg}. Apply this as the root screen background (edge-to-edge). Prefer a solid background (no blur/glass).`;
        if (light) {
            instructions += `\n- Theme: LIGHT. Do NOT use dark root backgrounds (avoid bg-black, bg-slate-900/950, bg-gray-900/950, or dark gradients).`;
            instructions += `\n- Contrast: use dark text by default (e.g. text-slate-900) and ensure good readability on ${bg}.`;
        } else {
            instructions += `\n- Theme: DARK. Keep the background dark (close to ${bg}) and ensure all text/icons are readable (use light text by default, e.g. text-slate-50).`;
        }
    }

    return `${userPrompt}${instructions}`;
}
